import QRCode from "react-qr-code";
import { Grid } from "@mui/material";
import { useEffect, useState } from "react";

import Box from "../Box";
import Modal from "../Modal";
import Button from "../Button";
import FormField from "../FormField";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import TwoFactorAuthService from "../../services/identity/twoFactorAuth.service";

import { IInput } from "../../commons/interfaces";

interface IModelSetupAuthenticator {
    isSetup: boolean;
    sharedKey: string,
    authenticatorUri: string,
}

interface IDetailTwoFactorAuthen {
    id: string,
    status?: number,
    userName?: string,
    emailConfirmed?: boolean,
    normalizedEmail?: string,
    email?: string,
    phoneNumber?: string,
    twoFactorEnabled?: boolean,
    hasAuthenticator?: boolean,
    twoFactorClientRemembered?: boolean,
    recoveryCodesLeft?: number,
    userProfile?: any,
    createTime?: string,
    createUser?: string,
    updateTime?: string,
    updateUser?: string,
    isExternalUser?: number,
    externalLogins?: string[]
}


interface I2FAProps {
    idHref: string;

    onError: (msg: string) => void;
    onSuccess: (msg: string) => void;
    onLoading: (value: boolean) => void;
}

const TwoFactorAuthentication = ({
    idHref,

    onError,
    onSuccess,
    onLoading,
}: I2FAProps) => {
    const strings = useStrings();
    const [twoFactorEnabled, setTwoFactorEnabled] = useState<boolean>(false);
    const [verificationCode, setVerificationCode] = useState<IInput>({} as IInput);
    const [dataDetail, setDataDetail] = useState<IDetailTwoFactorAuthen>({} as IDetailTwoFactorAuthen);
    const [dataSetup, setDataSetup] = useState<IModelSetupAuthenticator>({} as IModelSetupAuthenticator);

    useEffect(() => { getDetail() }, []);

    const getDetail = async () => {
        try {
            const result = await TwoFactorAuthService.details();

            const newdata: IDetailTwoFactorAuthen = {
                ...result
            };

            setDataDetail(newdata);
            setTwoFactorEnabled(newdata.twoFactorEnabled || false);

        } catch (error) { }
    }

    const handleSetupAuthenticator = async () => {
        try {
            onLoading(true);

            const result = await TwoFactorAuthService.setupAuthenticator();
            if (result?.statusCode === 200) {
                setDataSetup({
                    isSetup: true,
                    sharedKey: result?.result?.sharedKey,
                    authenticatorUri: result?.result?.authenticatorUri,
                });
            } else {
                setDataSetup({
                    isSetup: false,
                    sharedKey: "",
                    authenticatorUri: "",
                });
            }

            onLoading(false);
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    }

    const handleVerifyAuthenticator = () => {
        if (Helpers.isNullOrEmpty(verificationCode.value)) {
            setVerificationCode({ error: strings.Common.REQUIRED });
        } else {
            Helpers.showConfirmAlert(strings.TwoFactorAuth.CONFIRM_VERIFY_TFA,
                async () => {
                    try {
                        onLoading(true);

                        const result = await TwoFactorAuthService.verifyAuthenticator({ verificationCode: verificationCode.value });
                        if (result?.statusCode === 200) {
                            await getDetail();

                            setDataSetup({
                                isSetup: false,
                                sharedKey: "",
                                authenticatorUri: "",
                            });

                            onSuccess(strings.TwoFactorAuth.VERIFY_TFA_SUCCESS);
                        }

                        onLoading(false);
                    } catch (error) {
                        const e = Helpers.renderExceptionError(error);
                        onError(e);
                    }
                }
            );
        }
    }

    const handleResetCode = () => {
        Helpers.showConfirmAlert(strings.TwoFactorAuth.CONFIRM_RESET_CODE,
            async () => {
                try {
                    onLoading(true);

                    const result = await TwoFactorAuthService.resetAuthenticator();
                    if (result?.statusCode === 200) {
                        await getDetail();
                        onSuccess(strings.TwoFactorAuth.RESET_CODE_SUCCESS);
                    }

                    onLoading(false);
                } catch (error) {
                    const e = Helpers.renderExceptionError(error);
                    onError(e);
                }
            },
            () => { },
            "Ok",
        );
    }

    const handleDisableAuthenticator = () => {
        Helpers.showConfirmAlert(strings.TwoFactorAuth.CONFIRM_DISABLE_TFA,
            async () => {
                try {
                    onLoading(true);

                    const result = await TwoFactorAuthService.disableT2A();
                    if (result?.statusCode === 200) {
                        await getDetail();
                        onSuccess(strings.TwoFactorAuth.DISABLE_TFA_SUCCESS);
                    }

                    onLoading(false);
                } catch (error) {
                    const e = Helpers.renderExceptionError(error);
                    onError(e);
                }
            },
            () => { },
            "Ok",
        );
    }

    const renderModalVerifyAuthenticator = () => (
        <Modal
            fullWidth
            maxWidth="sm"
            hasActionButton
            title={strings.TwoFactorAuth.TITLE}
            visible={dataSetup.isSetup || false}
            buttonAction={strings.TwoFactorAuth.VERIFY}
            onClose={() => {
                setDataSetup({
                    isSetup: false,
                    sharedKey: "",
                    authenticatorUri: "",
                });
            }}
            onAction={() => handleVerifyAuthenticator()}
        >
            <Grid container spacing={1} p={2}>
                <Grid item xs={12}>
                    <Typography variant="button">{strings.TwoFactorAuth.CONTENT_SCAN_IMAGE}</Typography>
                </Grid>
                {
                    !Helpers.isNullOrEmpty(dataSetup.authenticatorUri) &&
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center">
                            <QRCode
                                size={125}
                                value={dataSetup.authenticatorUri}
                            />
                        </Box>
                    </Grid>
                }
                <Grid item xs={12}>
                    <Typography variant="button">
                        {`${strings.TwoFactorAuth.OR_ENTER_THE_SECRET} ${dataSetup.sharedKey}`}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="button">{strings.TwoFactorAuth.AND_ENTER_PASSCODE}</Typography>
                </Grid>
                <Grid item xs={12} mt={2} mb={1}>
                    <FormField
                        required
                        maxLength={6}
                        variant="outlined"
                        defaultValue={verificationCode?.value}
                        errorMessage={verificationCode?.error}
                        label={strings.TwoFactorAuth.PASS_CODE}
                        placeholder={strings.TwoFactorAuth.ENTER_PASS_CODE}
                        onBlur={(value: any) => { setVerificationCode({ value }) }}
                    />
                </Grid>
            </Grid>
        </Modal>
    );

    return (
        <Box key={`tfa`} id={idHref} p={2}>
            <Box display="flex">
                <Typography variant="h5">{strings.TwoFactorAuth.TITLE}</Typography>
                <Box
                    variant="gradient"
                    style={{
                        marginLeft: "16px",
                        textAlign: "center",
                        borderRadius: "10px",
                        padding: "6px 14px 4px 14px",
                    }} bgColor={twoFactorEnabled ? "success" : "secondary"} >
                    <Typography sx={{
                        fontSize: "11px",
                        color: "#ffffff",
                        lineHeight: 1.5,
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        letterSpacing: "0.02857em",
                        fontFamily: "Open Sans",
                    }}>
                        {twoFactorEnabled ? strings.TwoFactorAuth.AUTHENTICATED : strings.TwoFactorAuth.UN_AUTHENTICATED}
                    </Typography>
                </Box>
            </Box>
            {
                twoFactorEnabled
                    ? (
                        <Grid container spacing={1} p={2}>
                            <Grid item xs={12}>
                                <Typography variant="button">{strings.TwoFactorAuth.CONTENT_RESET_CODE}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Button color="info" onClick={() => handleResetCode()} >
                                    {strings.TwoFactorAuth.BTN_RESET_CODE}
                                </Button>
                            </Grid>
                            <Grid item xs={12} mt={2} mb={1}>
                                <Typography variant="button">{strings.TwoFactorAuth.CONTENT_DISABLE_TFA}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Button color="error" onClick={() => handleDisableAuthenticator()} >
                                    {strings.TwoFactorAuth.BTN_DISABLE_TFA}
                                </Button>
                            </Grid>
                        </Grid>
                    )
                    : (
                        <Grid container spacing={1} p={2}>
                            <Grid item xs={12}>
                                <Typography variant="button">{strings.TwoFactorAuth.CONTENT_1}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="button">{strings.TwoFactorAuth.CONTENT_2}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Button onClick={() => handleSetupAuthenticator()} >
                                    {strings.TwoFactorAuth.BTN_ENROLL_TFA}
                                </Button>
                            </Grid>
                        </Grid>
                    )
            }
            {renderModalVerifyAuthenticator()}
        </Box>
    );
};

export default TwoFactorAuthentication;
